import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Zap, FileText, Calculator, Users, Shield, TrendingUp, Star, Sparkles, Clock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [lifetimeBundleInfo, setLifetimeBundleInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState('monthly');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansResponse, lifetimeResponse] = await Promise.all([
          axios.get(`${API}/plans`),
          axios.get(`${API}/lifetime-bundle-info`)
        ]);
        setPlans(plansResponse.data);
        setLifetimeBundleInfo(lifetimeResponse.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: <Zap className="h-8 w-8 text-emerald-600" />,
      title: "Zeiterfassung mit Start/Stop",
      description: "Erfasse deine Arbeitszeit mit einem Klick und ordne sie direkt Projekten zu"
    },
    {
      icon: <FileText className="h-8 w-8 text-blue-600" />,
      title: "Professionelle Rechnungen (PDF)",
      description: "Erstelle rechtskonforme Rechnungen inkl. QR-Code für DE, AT und CH"
    },
    {
      icon: <Calculator className="h-8 w-8 text-purple-600" />,
      title: "Automatische Steuerberechnung",
      description: "Für Deutschland, Österreich und Schweiz – inkl. Kleinunternehmerregelung"
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-orange-600" />,
      title: "Echtzeit-Dashboard",
      description: "Behalte Einnahmen, Ausgaben und Steuern immer im Blick"
    },
    {
      icon: <Users className="h-8 w-8 text-cyan-600" />,
      title: "Kundenverwaltung",
      description: "Verwalte deine Kunden zentral mit allen wichtigen Daten"
    },
    {
      icon: <Shield className="h-8 w-8 text-green-600" />,
      title: "DSGVO-konform & sicher",
      description: "Hosting in Deutschland, vollständige DSGVO-Compliance"
    }
  ];

  const faqs = [
    {
      question: "Muss ich meine Kreditkarte für den kostenlosen Monat angeben?",
      answer: "Ja, für die Pläne Basic und Pro benötigen wir eine Zahlungsmethode. Der erste Monat ist jedoch kostenlos, und du kannst jederzeit vor Ablauf kündigen."
    },
    {
      question: "Kann ich jederzeit kündigen oder downgraden?",
      answer: "Ja, du kannst jederzeit kündigen oder zu einem günstigeren Plan wechseln. Die Änderung wird zum Ende deiner aktuellen Abrechnungsperiode (Monats- oder Jahresende) wirksam. Du behältst bis dahin vollen Zugriff auf alle Features."
    },
    {
      question: "Was passiert beim Upgrade auf einen höheren Plan?",
      answer: "Bei einem Upgrade zu einem höheren Plan (z.B. von Basic zu Pro) wird die Änderung sofort wirksam. Du zahlst anteilig für den Rest der aktuellen Periode."
    },
    {
      question: "Was passiert mit meinen Daten bei Kündigung?",
      answer: "Nach Kündigung wirst du automatisch auf den kostenlosen Free-Plan heruntergestuft. Deine Daten bleiben erhalten, aber du hast nur noch Zugriff auf die Features des Free-Plans (1 Kunde, 1 Projekt). Rechnungen bleiben aufgrund der gesetzlichen Aufbewahrungspflicht 10 Jahre gespeichert."
    },
    {
      question: "Ist das Lifetime Bundle wirklich lebenslang?",
      answer: "Ja! Einmal zahlen, für immer nutzen. Du erhältst alle Pro-Features ohne monatliche Kosten. Das Angebot gilt nur für die ersten 150 Early Supporters (50 zum Early Bird Preis, weitere 100 zum regulären Preis)."
    },
    {
      question: "Welche Zahlungsmethoden akzeptiert ihr?",
      answer: "Wir akzeptieren alle gängigen Kreditkarten (Visa, Mastercard, Amex) sowie SEPA-Lastschrift über unseren sicheren Zahlungspartner Stripe."
    },
    {
      question: "Gibt es einen Rabatt bei jährlicher Zahlung?",
      answer: "Ja! Bei jährlicher Zahlung sparst du ca. 17% im Vergleich zur monatlichen Zahlung. Basic Jahresabo: 90€ statt 108€ (9€×12), Pro Jahresabo: 190€ statt 228€ (19€×12)."
    },
    {
      question: "Was ist mit dem ersten Monat gratis gemeint?",
      answer: "Bei Abschluss eines Basic oder Pro Abos ist der erste Monat komplett kostenlos. Erst danach beginnt die reguläre Abrechnung. Du kannst jederzeit vor Ablauf des ersten Monats kündigen, ohne Kosten."
    }
  ];

  const getLifetimePrice = () => {
    if (!lifetimeBundleInfo) return '199';
    const sold = lifetimeBundleInfo.sold_count || 0;
    if (sold < 50) return '199';
    if (sold < 150) return '249';
    return null; // Sold out
  };

  const getLifetimeBadge = () => {
    if (!lifetimeBundleInfo) return '';
    const sold = lifetimeBundleInfo.sold_count || 0;
    if (sold < 50) return `Nur noch ${50 - sold} verfügbar`;
    if (sold < 150) return `Nur noch ${150 - sold} verfügbar`;
    return 'Ausverkauft';
  };

  const isLifetimeSoldOut = () => {
    if (!lifetimeBundleInfo) return false;
    return (lifetimeBundleInfo.sold_count || 0) >= 150;
  };

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleLifetimePurchase = async () => {
    setLoading(true);
    try {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      
      if (!token) {
        // Not logged in - redirect to register with intent
        navigate('/register?intent=lifetime');
        return;
      }

      // User is logged in - create checkout session
      const response = await axios.post(
        `${API}/lifetime-bundle/checkout`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Lifetime bundle checkout error:', error);
      if (error.response?.status === 401) {
        // Token invalid - redirect to register with intent
        navigate('/register?intent=lifetime');
      } else {
        alert('Fehler beim Laden des Checkouts. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const lifetimePrice = getLifetimePrice();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Calculator className="h-8 w-8 text-emerald-600" />
            <span className="text-xl font-bold text-gray-900">ivoliq</span>
          </div>
          <div className="flex gap-3">
            {isAuthenticated ? (
              <>
                <span className="text-sm text-gray-600 self-center">Hallo, {user?.name || user?.email}</span>
                <Button className="bg-emerald-600 hover:bg-emerald-700" asChild>
                  <Link to="/dashboard">Zur App</Link>
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/login">Anmelden</Link>
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" asChild>
                  <Link to="/register">Kostenlos starten</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-white border-b">
        <div className="container mx-auto px-4 py-16 lg:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-emerald-50 text-emerald-700 border-emerald-200">
              <Sparkles className="h-3 w-3 mr-1" />
              Erster Monat gratis – keine Kreditkarte nötig
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Zeiterfassung & Rechnungen für Freelancer
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Erfasse Zeiten, erstelle rechtskonforme Rechnungen und behalte deine Steuern im Blick – alles in einer App.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
              <Button size="lg" onClick={handleGetStarted} className="bg-emerald-600 hover:bg-emerald-700 text-base">
                Kostenlos starten
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" asChild className="text-base">
                <a href="#pricing">Preise ansehen</a>
              </Button>
            </div>

            {/* Lifetime Bundle Teaser */}
            {!isLifetimeSoldOut() && lifetimePrice && (
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 px-6 py-3 rounded-lg">
                <Star className="h-4 w-4 text-amber-600" />
                <span className="font-medium">Early Supporter: Lifetime Zugang für nur {lifetimePrice}€</span>
                <Badge variant="outline" className="ml-2 text-xs border-amber-400 text-amber-700">
                  {getLifetimeBadge()}
                </Badge>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Alles was du brauchst</h2>
            <p className="text-lg text-gray-600">Leistungsstarke Features für dein Business</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="mb-3">{feature.icon}</div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap & Feedback Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">
              Kontinuierliche Weiterentwicklung
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Wir hören auf dich</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              ivoliq wird aktiv weiterentwickelt – basierend auf deinem Feedback. Deine Ideen fließen direkt in unsere Produktpipeline ein.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                  Produktpipeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Transparente Roadmap mit kommenden Features</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Regelmäßige Updates und Verbesserungen</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Neue Funktionen basierend auf echten Bedürfnissen</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-6 w-6 text-purple-600" />
                  Dein Feedback
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Direkter Kontakt zum Entwicklerteam</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Feature-Requests werden gehört und umgesetzt</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Community-getriebene Entwicklung</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-white py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Transparente Preise</h2>
            <p className="text-lg text-gray-600 mb-6">Wähle den Plan, der zu dir passt</p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 mb-4">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingInterval === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monatlich
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingInterval === 'yearly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Jährlich
                <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-xs">17% sparen</Badge>
              </button>
            </div>
            
            <div className="mb-4">
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                Erster Monat gratis für Basic & Pro
              </Badge>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {/* Free Plan */}
          <Card className="bg-white hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="text-center">
                <CardTitle className="text-xl mb-2">Free</CardTitle>
                <div className="text-3xl font-bold text-gray-900 mb-1">0€</div>
                <p className="text-xs text-gray-500">Kostenlos</p>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>1 Kunde</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>1 Projekt</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Basis-Features</span>
                </li>
              </ul>
              <Button className="w-full" variant="outline" onClick={handleGetStarted}>
                Starten
              </Button>
            </CardContent>
          </Card>

          {/* Basic Plan */}
          <Card className="bg-white hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="text-center">
                <CardTitle className="text-xl mb-2">Basic</CardTitle>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {billingInterval === 'monthly' ? '9€' : '90€'}
                </div>
                <p className="text-xs text-gray-500">
                  {billingInterval === 'monthly' ? 'pro Monat' : 'pro Jahr'}
                </p>
                {billingInterval === 'yearly' && (
                  <p className="text-xs text-emerald-600 font-medium mt-1">~7,50€/Monat</p>
                )}
                <Badge className="mt-2 text-xs bg-emerald-50 text-emerald-700 border-emerald-200">1. Monat gratis</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>2 Kunden</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>4 Projekte</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Dashboard</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Auto-Rechnungen</span>
                </li>
              </ul>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleGetStarted}>
                Starten
              </Button>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className="bg-white border-2 border-emerald-500 shadow-md relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-emerald-600 text-white text-xs">Beliebt</Badge>
            </div>
            <CardHeader>
              <div className="text-center">
                <CardTitle className="text-xl mb-2">Pro</CardTitle>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {billingInterval === 'monthly' ? '19€' : '190€'}
                </div>
                <p className="text-xs text-gray-500">
                  {billingInterval === 'monthly' ? 'pro Monat' : 'pro Jahr'}
                </p>
                {billingInterval === 'yearly' && (
                  <p className="text-xs text-emerald-600 font-medium mt-1">~15,83€/Monat</p>
                )}
                <Badge className="mt-2 text-xs bg-emerald-50 text-emerald-700 border-emerald-200">1. Monat gratis</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span><strong>Unbegrenzt</strong> Kunden</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span><strong>Unbegrenzt</strong> Projekte</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Alle Features</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Steuerübersicht</span>
                </li>
              </ul>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleGetStarted}>
                Starten
              </Button>
            </CardContent>
          </Card>

          {/* Lifetime Bundle */}
          <Card className={`${isLifetimeSoldOut() ? 'opacity-60' : 'border-2 border-amber-400 shadow-md bg-amber-50/50'} relative`}>
            {!isLifetimeSoldOut() && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-amber-600 text-white text-xs flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Early Supporter
                </Badge>
              </div>
            )}
            <CardHeader>
              <div className="text-center">
                <CardTitle className="text-xl mb-2">Lifetime</CardTitle>
                {isLifetimeSoldOut() ? (
                  <div className="text-2xl font-bold text-gray-500 mb-1">Ausverkauft</div>
                ) : (
                  <>
                    <div className="text-3xl font-bold text-amber-700 mb-1">
                      {lifetimePrice}€
                    </div>
                    <p className="text-xs text-gray-600">Einmalig</p>
                    <Badge variant="outline" className="mt-2 text-xs border-amber-400 text-amber-700">
                      {getLifetimeBadge()}
                    </Badge>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span><strong>Alle Pro Features</strong></span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span><strong>Lebenslang</strong></span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Alle Updates</span>
                </li>
                <li className="flex items-start text-sm">
                  <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Nie wieder zahlen</span>
                </li>
              </ul>
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white" 
                onClick={handleLifetimePurchase}
                disabled={isLifetimeSoldOut() || loading}
              >
                {loading ? 'Lädt...' : isLifetimeSoldOut() ? 'Ausverkauft' : 'Sichern'}
              </Button>
            </CardContent>
          </Card>
          </div>

          <p className="text-center text-sm text-gray-600 mt-8">
            Alle Preise verstehen sich zzgl. MwSt. • Jährliche Zahlung: ca. 21% Rabatt
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Häufige Fragen</h2>
            <p className="text-lg text-gray-600">Alles was du wissen musst</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">Bereit durchzustarten?</h2>
        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          Starte noch heute und erlebe, wie einfach Freelancing sein kann
        </p>
        <Button size="lg" onClick={handleGetStarted} className="text-lg px-8">
          Kostenlos registrieren
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="h-7 w-7 text-emerald-500" />
                <span className="text-lg font-bold">ivoliq</span>
              </div>
              <p className="text-gray-400 text-sm">
                Zeiterfassung & Rechnungen für Freelancer
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Produkt</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Preise</a></li>
                <li><Link to="/register" className="hover:text-white">Registrieren</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/privacy" className="hover:text-white">Datenschutz</Link></li>
                <li><Link to="/terms" className="hover:text-white">AGB</Link></li>
                <li><Link to="/impressum" className="hover:text-white">Impressum</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="mailto:support@ivoliq.io" className="hover:text-white">support@ivoliq.io</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 ivoliq. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
